import {
    PROVIDER_SEARCH_SERVICE_ERROR_CODES,
    ERROR_CODE_HEADER,
    ERROR_MESSAGE_HEADER,
    MICRO_FRONTEND_STARGATE_KEY,
} from '../constants/index';
import { StatusCodes } from 'http-status-codes';
import { getAccessToken } from '../utils/Auth.util';
import axios from 'axios';
import { getCoverageFromMemberDetails } from '../utils/SearchPageUtil';
import { mapProviders } from '../utils/ProviderUtil';

const genericErrorResponse = {
    providerSearchResponse: null,
    errorMessage: 'An error occurred, please try again later.',
};

/**
 * Generates the parameters we send to CPTT Prov Search through to PES Practitioner/Organization Prov Search
 * @param {Object} memberDetails - benefits response
 * @param {String} policyId - group number
 * @param {Object} selectedService - is one of the shoppable services
 * @param {Object} locationOfService - is a location object from which we can derive lat/lng data (.value.center[0] is lng, .value.center[1] is lat)
 * @param {Number} radius - for the search radius
 * @param {String} providerName - string, can be empty (for an open search)
 * @param {String} carePathStepId - from the shoppable service, is an identifier for a particular service/part of a carepath
 * @param {String} carePathCategoryType - from the shoppable service, indicates whether it is medical/behavioral/dental/etc.
 * @param {String} providerType - from the shoppable service, indicates whether the service is provided by an individual/place/person/etc.
 * @returns query params to be sent through to PES Prov Search
 */
export const generatePractOrgQueryParams = (
    memberDetails,
    selectedService,
    locationOfService,
    radius,
    providerName,
    carePathStepId,
    carePathCategoryType,
    providerType
) => {
    return {
        providerName: providerName, // optional
        locationInfo: {
            // lngLat is the mapbox ordering for coordinate pairs https://docs.mapbox.com/mapbox-gl-js/api/geography/#lnglat
            lng: locationOfService.value.center[0], // required
            lat: locationOfService.value.center[1], // required
            radius: radius?.value?.value || radius || 100, // optional
        },
        carePathDetails: {
            procedureCode: selectedService.value?.lineItem?.billingCode?.code || '', // required if no specialtyCode
            procedureTypeCode: selectedService.value?.lineItem?.billingCode?.system || 'CPT', // required if no specialtyCode
            carePathStepId: carePathStepId, // optional if specialtyCode, maybe we can derive on mapping on prov search?
            carePathCategoryType: carePathCategoryType, // optional if specialtyCode, maybe we can derive on mapping on prov search?
            providerType: providerType, // optional if specialtyCode, maybe we can derive on mapping on prov search?
            specialtyCode: '', // TODO depends on mapping we from sarma, what do we pass? Anesthesia?
        },
        memberDetails: memberDetails, // required
    };
};

export const buildHospitalAffiliationRequest = (practOrgQueryParams, filteredProviderData) => {
    delete practOrgQueryParams.providerName;

    const hospitalsToSearch = [];

    filteredProviderData.forEach((provider) => {
        let provId = provider?.prefixId ?? provider?.mpin;
        provider.locAndPractRole.forEach((locAndPract) => {
            locAndPract?.hospitalAffiliations?.forEach((hospital, i) => {
                const existingHospital = hospitalsToSearch.find((h) => h?.id === hospital?.id);
                if (existingHospital) {
                    if (existingHospital?.affiliatedPractitionerIds.indexOf(provId) === -1) {
                        existingHospital?.affiliatedPractitionerIds.push(provId);
                    }
                } else {
                    hospitalsToSearch.push({ ...hospital, affiliatedPractitionerIds: [provId] });
                }
            });
        });
    });

    return {
        ...practOrgQueryParams,
        hospitalsToSearch: hospitalsToSearch,
    };
};

export const mapFacilitiesToPhysicians = (filteredProviderData, affiliatedHospitals) => {
    filteredProviderData.forEach((provider) => {
        if (affiliatedHospitals[provider.prefixId]) {
            const transformedProviderData = mapProviders(affiliatedHospitals[provider.prefixId]);
            provider['facilitiesData'] = transformedProviderData;
        } else {
            provider['facilitiesData'] = [];
        }
    });
    return filteredProviderData;
};

// Helper method for provider search
export const postProviderSearch = async (url, payload, cpttCorrelationID = null) => {
    try {
        const pesToken = await getAccessToken(
            process.env.ENV == 'uat' ? 'Graphql_NonProd_token' : 'Graphql_token',
            process.env.ACCESS_TOKENS_URL_NONPROD
        );
        const stargateToken = await getAccessToken(MICRO_FRONTEND_STARGATE_KEY);
        const headers = buildHeaders(cpttCorrelationID, stargateToken, pesToken);
        const response = await axios.post(url, JSON.stringify(payload), {
            headers,
        });

        let providerSearchResponse;
        let errorMessage;
        // UMR indemnity plans have a null PNI, set in benefits service
        const indemnityExemption =
            payload?.memberDetails?.sourceCode == 'UM' &&
            payload?.memberDetails?.coverageInfo?.indemnityFlag;

        // check for no content - log & return
        if (response.status === StatusCodes.NO_CONTENT) {
            errorMessage = determineNoContentError(response.headers);
            return { providerSearchResponse, errorMessage };
        }

        // check for partial content - log & determine whether to return or continue
        if (response.status === StatusCodes.PARTIAL_CONTENT) {
            errorMessage = determinePartialContentError(response.headers);
            if (errorMessage) {
                return { providerSearchResponse, errorMessage };
            }
        }

        providerSearchResponse = response.data;

        providerSearchResponse.noCoverageDisclaimerFlag = !providerSearchResponse.hasValidCoverages;
        providerSearchResponse.innNoNetworkDisclaimerFlag = !(
            providerSearchResponse.hasPlanNetworkIndicators || indemnityExemption
        );

        return {
            providerSearchResponse,
            errorMessage: null,
        };
    } catch (error) {
        console.error(
            `Call to Provider Search Service postProviderSearch endpoint failed with error ${error.message}`
        );

        genericErrorResponse.providerSearchResponse = error?.response?.data;

        genericErrorResponse.providerSearchResponse.noCoverageDisclaimerFlag =
            genericErrorResponse.providerSearchResponse.hasValidCoverages == null
                ? false
                : !genericErrorResponse.providerSearchResponse.hasValidCoverages;
        genericErrorResponse.providerSearchResponse.innNoNetworkDisclaimerFlag =
            genericErrorResponse.providerSearchResponse.hasPlanNetworkIndicators == null
                ? false
                : !genericErrorResponse.providerSearchResponse.hasPlanNetworkIndicators;

        return { genericErrorResponse };
    }
};

// Helper method for hospital affiliations search
export const postAffiliatedHospitalsSearch = async (url, payload, cpttCorrelationID = null) => {
    try {
        const pesToken = await getAccessToken(
            'Graphql_token',
            process.env.ACCESS_TOKENS_URL_NONPROD
        );
        const stargateToken = await getAccessToken(MICRO_FRONTEND_STARGATE_KEY);
        const headers = buildHeaders(cpttCorrelationID, stargateToken, pesToken);
        const response = await axios.post(url, JSON.stringify(payload), {
            headers,
        });

        const affiliatedProvidersResp = response?.data?.hospitalsByPractitionerId;

        return affiliatedProvidersResp;
    } catch (error) {
        console.error(
            `Call to Provider Search Service postProviderSearch endpoint failed with error ${error.message}`
        );

        return [];
    }
};

const buildHeaders = (cpttCorrelationID, stargateToken, pesToken) => {
    const headers = {
        'Content-Type': 'application/json',
        'x-epe-correlationid': cpttCorrelationID,
        Authorization: 'Bearer ' + stargateToken,
        'x-pes-oauth-token': pesToken,
    };
    if (process.env.ENV === 'uat') {
        headers['x-pes-upstream-env'] = 'bravo';
    }

    return headers;
};

/**
 * When Provider Search Service returns with a 204 No Content Status,
 * determine what error occured based on the response headers, set error message to be displayed accordingly
 * @param {object} headers Provider Search Service response headers that include x-epe-error-code header, a comma delimited list of error codes
 * @returns {string} errorMessage to be displayed in the error Snackbar that appears on Search Page
 */
const determineNoContentError = (headers) => {
    const errorCodes = headers?.[ERROR_CODE_HEADER];
    let errorMessage = genericErrorResponse.errorMessage;
    if (errorCodes) {
        const errorCodesArray = errorCodes.split(',');
        PROVIDER_SEARCH_SERVICE_ERROR_CODES.PROVIDERS_NOT_FOUND.code.forEach((code) => {
            if (errorCodesArray?.includes(code)) {
                errorMessage =
                    PROVIDER_SEARCH_SERVICE_ERROR_CODES.PROVIDERS_NOT_FOUND.displayMessage;
                console.error(errorMessage);
            }
        });
    }
    return errorMessage;
};

/**
 * When Provider Search Service returns with a 206 Partial Content Status, determine what error occured based on the response headers
 * If it is a "breaking" error, i.e. we should not let member continue, set error message and set boolean to return with error
 * @param {object} headers Provider Search Service response headers that include x-epe-error-code header, a comma delimited list of error codes
 * @returns {string} errorMessage (if applicable) to be displayed in error Snackbar that appears on Search Page, else null
 */
const determinePartialContentError = (headers) => {
    const errorCodes = headers?.[ERROR_CODE_HEADER];
    console.error(headers?.[ERROR_MESSAGE_HEADER]);
    let errorMessage;
    if (errorCodes) {
        const errorCodesArray = errorCodes.split(',');
        PROVIDER_SEARCH_SERVICE_ERROR_CODES.NO_NETWORKS_OR_COVERAGES.code.forEach((code) => {
            if (errorCodesArray?.includes(code)) {
                errorMessage =
                    PROVIDER_SEARCH_SERVICE_ERROR_CODES.NO_NETWORKS_OR_COVERAGES.displayMessage;
            }
        });
    }

    return errorMessage;
};

export const getMemberDetailsForProviderSearch = (memberDetails, carePathCategoryType) => {
    return {
        sourceCode: memberDetails.sourceCode,
        coverageInfo: getCoverageFromMemberDetails(memberDetails, carePathCategoryType),
        lineOfBusiness: memberDetails.lineOfBusiness,
        policyId: memberDetails.policyNumber,
        popConfig: memberDetails.popConfig,
        membershipCategory: memberDetails.membershipCategory ?? '',
    };
};
